import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import unitsService from '../../services/unitsService';
import unitTypesService from '../../services/unitTypesService';
import {
  UNIT_STATUS_TILE, UNIT_STATUS_TILE_HOVER, UNIT_STATUS_LEGEND,
  UNIT_STATUS_FALLBACK_TILE, tileSwatch,
} from '../../utils/unitStatusColors';

/**
 * Grid view of all units, grouped by unit type, color-coded by status.
 *
 * Matches the ESS / Storable Easy units page UX: section header per unit
 * type, dense grid of small colored cells, click a cell to jump to the
 * unit's detail page. Operator sees 60+ units in one scan.
 */

// Colours + legend come from the shared unit-status palette so this grid,
// the site map and the list cannot disagree about what a colour means. This
// file used to paint Available BLUE and Occupied dark slate — the exact
// inverse of the map — so a unit changed meaning between tabs.
const STATUS_STYLES = UNIT_STATUS_TILE;
const LEGEND = UNIT_STATUS_LEGEND;

const normType = (s) =>
  String(s || '').toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();

const UnitsGrid = ({ facilityId }) => {
  const [units, setUnits] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  // Section refs keyed by normalized type name so a deep-link can scroll
  // straight to a type (e.g. from the dashboard occupancy-breakdown bars)
  // instead of dumping the operator at the top of a long grid.
  const sectionRefs = useRef({});
  const targetType = normType(
    searchParams.get('type') || searchParams.get('size') || '',
  );

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, typesRes] = await Promise.all([
        unitsService.getAll(facilityId),
        unitTypesService.getAll(facilityId),
      ]);
      setUnits(unitsRes.data || []);
      setUnitTypes(typesRes.data || []);
    } catch (err) {
      console.error('UnitsGrid load error:', err);
      setError('Could not load units.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  // Group units by unit_type_id (preserves the catalog order from the Unit
  // Types tab), then within each type sub-group by alphabet prefix so
  // operators can scan e.g. "all P-prefix parking spots" or "all W-prefix
  // warehouse bays" at a glance. Pure-numeric units (1, 2, 3...) become
  // their own sub-group sorted numerically. Letter-prefixed units (W1, P1,
  // B2, B3) get one sub-group per letter, sorted by the trailing number.
  // Legacy units with no unit_type_id fall into an "Unclassified" type
  // bucket so they remain visible.
  const grouped = useMemo(() => {
    const byTypeId = new Map();
    for (const t of unitTypes) {
      byTypeId.set(t._id, { type: t, units: [] });
    }
    const unclassified = [];
    for (const u of units) {
      if (u.is_archived) continue;
      if (u.unit_type_id && byTypeId.has(u.unit_type_id)) {
        byTypeId.get(u.unit_type_id).units.push(u);
      } else {
        unclassified.push(u);
      }
    }
    // Split a unit_number like "P3" into { prefix: "P", number: 3 } or
    // "12" into { prefix: "", number: 12 }. Mixed strings like "ABC"
    // become { prefix: "ABC", number: NaN } — they sort lexicographically.
    const PREFIX_RE = /^([A-Za-z]*)(\d+)?$/;
    const parseUnitNumber = (raw) => {
      const m = PREFIX_RE.exec(String(raw || ''));
      if (!m) return { prefix: String(raw || ''), number: NaN };
      return {
        prefix: (m[1] || '').toUpperCase(),
        number: m[2] != null ? Number(m[2]) : NaN,
      };
    };
    const subGroupUnits = (unitList) => {
      // Returns: [{ key, label, units }] ordered: numeric-only group first
      // (key=""), then letter groups A, B, C, ... alphabetical. Letter
      // groups labeled "P units", "W units", etc.
      const buckets = new Map();
      for (const u of unitList) {
        const { prefix, number } = parseUnitNumber(u.unit_number);
        if (!buckets.has(prefix)) buckets.set(prefix, []);
        buckets.get(prefix).push({ ...u, _parsed: { prefix, number } });
      }
      // Sort each bucket by parsed number, then by raw string as tiebreak.
      for (const [, list] of buckets) {
        list.sort((a, b) => {
          const na = a._parsed.number;
          const nb = b._parsed.number;
          const aHas = Number.isFinite(na);
          const bHas = Number.isFinite(nb);
          if (aHas && bHas) return na - nb;
          if (aHas) return -1;
          if (bHas) return 1;
          return String(a.unit_number).localeCompare(String(b.unit_number));
        });
      }
      // Order buckets: empty-prefix (numeric-only) first, then letters
      // alphabetically. Skip empty sub-groups.
      const keys = [...buckets.keys()].sort((a, b) => {
        if (a === '' && b !== '') return -1;
        if (b === '' && a !== '') return 1;
        return a.localeCompare(b);
      });
      return keys
        .filter((k) => buckets.get(k).length > 0)
        .map((k) => ({
          key: k || 'numeric',
          label: k ? `${k} units` : null, // null = no sub-header for the numeric group
          units: buckets.get(k),
        }));
    };

    const result = [];
    for (const t of unitTypes) {
      const g = byTypeId.get(t._id);
      result.push({
        ...g,
        subGroups: subGroupUnits(g.units),
      });
    }
    if (unclassified.length) {
      result.push({
        type: { _id: 'unclassified', name: 'Unclassified', monthly_rate: null },
        units: unclassified,
        subGroups: subGroupUnits(unclassified),
        isUnclassified: true,
      });
    }
    return result;
  }, [units, unitTypes]);

  // Arrived with ?type=/?size= (e.g. clicked a dashboard occupancy bar) →
  // scroll that type's section into view once the grid has rendered.
  useEffect(() => {
    if (loading || !targetType) return;
    const el = sectionRefs.current[targetType];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, targetType, grouped]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">
        {error}
      </div>
    );
  }

  if (!units.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
        <h3 className="text-base font-semibold text-slate-700 mb-1">
          No units yet
        </h3>
        <p className="text-[13px] text-slate-500 mb-4">
          Define your unit types and bulk-add units to populate this grid.
        </p>
        <Link
          to="/units?view=types"
          className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700"
        >
          Define Unit Types
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-6 text-[12px] text-slate-600">
        <span className="font-medium text-slate-700">Status:</span>
        {LEGEND.map((l) => (
          <span key={l.status} className="inline-flex items-center gap-1.5">
            <span
              className={`inline-block w-3 h-3 rounded border ${tileSwatch(l.status)}`}
            />
            {l.label}
          </span>
        ))}
      </div>

      {/* Type groups */}
      <div className="space-y-7">
        {grouped.map((group) => (
          <section
            key={group.type._id}
            ref={(el) => { sectionRefs.current[normType(group.type.name)] = el; }}
          >
            <div className="flex items-baseline justify-between mb-2.5">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900">
                  {group.type.name}
                </h2>
                <p className="text-[12px] text-slate-400 mt-0.5">
                  {group.units.length} unit
                  {group.units.length === 1 ? '' : 's'}
                  {group.type.monthly_rate != null && (
                    <>
                      {' · '}${Number(group.type.monthly_rate).toFixed(2)}/mo
                    </>
                  )}
                </p>
              </div>
              {!group.isUnclassified && (
                <Link
                  to={`/units?view=types`}
                  className="text-[12px] text-slate-400 hover:text-blue-600"
                >
                  Edit type
                </Link>
              )}
            </div>

            {group.units.length === 0 ? (
              <p className="text-[12px] text-slate-400 italic px-2">
                No units of this type yet.
              </p>
            ) : (
              <div className="space-y-3">
                {group.subGroups.map((sg) => (
                  <div key={sg.key}>
                    {sg.label && (
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">
                        {sg.label}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {sg.units.map((unit) => {
                        const style = `border ${
                          STATUS_STYLES[unit.status] || UNIT_STATUS_FALLBACK_TILE
                        } ${UNIT_STATUS_TILE_HOVER[unit.status] || ''}`;
                        return (
                          <Link
                            key={unit._id}
                            to={`/units/${unit._id}`}
                            title={`Unit ${unit.unit_number} — ${unit.status}`}
                            className={`inline-flex items-center justify-center min-w-[44px] h-10 px-2 rounded-md text-[12px] font-semibold transition-colors ${style}`}
                          >
                            {unit.unit_number}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default UnitsGrid;
